/** @jsx jsx */
import { jsx } from '@emotion/react';
import { defineMessages } from 'react-intl-next';
import { TableSortOrder as SortOrder } from '@atlaskit/adf-schema/steps';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { addColumnAfter, addRowAfter, backspace, tooltip } from '@atlaskit/editor-common/keymaps';
import commonMessages from '@atlaskit/editor-common/messages';
import { cellBackgroundColorPalette, DEFAULT_BORDER_COLOR } from '@atlaskit/editor-common/ui-color';
import { closestElement, getChildrenInfo as _getChildrenInfo, getNodeName, isReferencedSource } from '@atlaskit/editor-common/utils';
import { findParentDomRefOfType } from '@atlaskit/editor-prosemirror/utils';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
import { shortcutStyle } from '@atlaskit/editor-shared-styles/shortcut';
import { findCellRectClosestToPos, findTable, getSelectionRect, isSelectionType, splitCell } from '@atlaskit/editor-tables/utils';
import RemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import { clearHoverSelection, hoverColumns, hoverMergedCells, hoverRows, hoverTable, removeDescendantNodes } from './commands';
import { deleteColumnsWithAnalytics, deleteRowsWithAnalytics, deleteTableWithAnalytics, distributeColumnsWidthsWithAnalytics, emptyMultipleCellsWithAnalytics, insertColumnWithAnalytics, insertRowWithAnalytics, mergeCellsWithAnalytics, setColorWithAnalytics, sortColumnWithAnalytics, splitCellWithAnalytics, toggleHeaderColumnWithAnalytics, toggleHeaderRowWithAnalytics, toggleNumberColumnWithAnalytics, wrapTableInExpandWithAnalytics } from './commands-with-analytics';
import { getPluginState } from './pm-plugins/plugin-factory';
import { pluginKey as tableResizingPluginKey } from './pm-plugins/table-resizing';
import { getNewResizeStateFromSelectedColumns } from './pm-plugins/table-resizing/utils/resize-state';
import { pluginKey as tableWidthPluginKey } from './pm-plugins/table-width';
import { canMergeCells } from './transforms';
import { TableCssClassName } from './types';
import { messages as ContextualMenuMessages } from './ui/FloatingContextualMenu/ContextualMenu';
import tableMessages from './ui/messages';
import { getMergedCellsPositions, getSelectedColumnIndexes, getSelectedRowIndexes } from './utils';
export var messages = defineMessages({
  tableOptions: {
    id: 'fabric.editor.tableOptions',
    defaultMessage: 'Table options',
    description: 'Opens a menu with additional table options'
  },
  headerRow: {
    id: 'fabric.editor.headerRow',
    defaultMessage: 'Header row',
    description: 'Marks the first table row as a header row'
  },
  headerColumn: {
    id: 'fabric.editor.headerColumn',
    defaultMessage: 'Header column',
    description: 'Marks the first table column as a header row'
  },
  numberedColumn: {
    id: 'fabric.editor.numberedColumn',
    defaultMessage: 'Numbered column',
    description: 'Adds an auto-numbering column to your table'
  },
  collapseTable: {
    id: 'fabric.editor.collapseTable',
    defaultMessage: 'Collapse table',
    description: 'Wraps table in an expand'
  }
});
export var getToolbarMenuConfig = function getToolbarMenuConfig(config, state, _ref, editorAnalyticsAPI) {
  var formatMessage = _ref.formatMessage;
  var options = [{
    id: 'editor.table.headerRow',
    title: formatMessage(messages.headerRow),
    onClick: toggleHeaderRowWithAnalytics(editorAnalyticsAPI),
    selected: state.isHeaderRowEnabled,
    hidden: !config.allowHeaderRow
  }, {
    id: 'editor.table.headerColumn',
    title: formatMessage(messages.headerColumn),
    onClick: toggleHeaderColumnWithAnalytics(editorAnalyticsAPI),
    selected: state.isHeaderColumnEnabled,
    hidden: !config.allowHeaderColumn
  }, {
    id: 'editor.table.numberedColumn',
    title: formatMessage(messages.numberedColumn),
    onClick: toggleNumberColumnWithAnalytics(editorAnalyticsAPI),
    selected: state.isNumberColumnEnabled,
    hidden: !config.allowNumberColumn
  }, {
    id: 'editor.table.collapseTable',
    title: formatMessage(messages.collapseTable),
    onClick: wrapTableInExpandWithAnalytics(editorAnalyticsAPI),
    selected: !!state.isTableCollapsed,
    disabled: !state.canCollapseTable,
    hidden: !config.allowCollapse
  }];
  return {
    id: 'editor.table.tableOptions',
    type: 'dropdown',
    testId: 'table_options',
    title: formatMessage(messages.tableOptions),
    hidden: options.every(function (option) {
      return option.hidden;
    }),
    options: options
  };
};

// Added these options for mobile. Mobile bridge translates this menu and
// relay it to the native mobile. Native mobile displays the menu
// with native widgets. It's enabled via a plugin config.
export var getToolbarCellOptionsConfig = function getToolbarCellOptionsConfig(editorState, editorView, initialSelectionRect, _ref2, getEditorContainerWidth, editorAnalyticsAPI) {
  var _pluginState$pluginCo, _pluginState$pluginCo2;
  var formatMessage = _ref2.formatMessage;
  var top = initialSelectionRect.top,
    bottom = initialSelectionRect.bottom,
    right = initialSelectionRect.right,
    left = initialSelectionRect.left;
  var numberOfColumns = right - left;
  var numberOfRows = bottom - top;
  var pluginState = getPluginState(editorState);
  var options = [{
    id: 'editor.table.insertColumn',
    title: formatMessage(tableMessages.insertColumn),
    onClick: function onClick(state, dispatch, view) {
      var selectionRect = getClosestSelectionRect(state);
      var index = selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.right;
      if (index) {
        insertColumnWithAnalytics(getEditorContainerWidth, editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, index)(state, dispatch, view);
      }
      return true;
    },
    selected: false,
    disabled: false,
    elemAfter: jsx("div", {
      css: shortcutStyle
    }, tooltip(addColumnAfter))
  }, {
    id: 'editor.table.insertRow',
    title: formatMessage(tableMessages.insertRow),
    onClick: function onClick(state, dispatch) {
      var selectionRect = getClosestSelectionRect(state);
      var index = selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.bottom;
      if (index) {
        insertRowWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, {
          index: index,
          moveCursorToInsertedRow: true
        })(state, dispatch);
      }
      return true;
    },
    selected: false,
    disabled: false,
    elemAfter: jsx("div", {
      css: shortcutStyle
    }, tooltip(addRowAfter))
  }, {
    id: 'editor.table.removeColumns',
    title: formatMessage(tableMessages.removeColumns, {
      0: numberOfColumns
    }),
    onClick: function onClick(state, dispatch, view) {
      var selectionRect = getClosestSelectionRect(state);
      if (selectionRect) {
        deleteColumnsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, selectionRect)(state, dispatch, view);
      }
      return true;
    },
    onFocus: highlightColumnsHandler,
    onBlur: clearHoverSelection(),
    onMouseOver: highlightColumnsHandler,
    onMouseLeave: clearHoverSelection(),
    selected: false,
    disabled: false
  }, {
    id: 'editor.table.removeRows',
    title: formatMessage(tableMessages.removeRows, {
      0: numberOfRows
    }),
    onClick: function onClick(state, dispatch) {
      var selectionRect = getClosestSelectionRect(state);
      if (selectionRect) {
        deleteRowsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, selectionRect, false)(state, dispatch);
      }
      return true;
    },
    onFocus: highlightRowsHandler,
    onBlur: clearHoverSelection(),
    onMouseOver: highlightRowsHandler,
    onMouseLeave: clearHoverSelection(),
    selected: false,
    disabled: false
  }, {
    id: 'editor.table.mergeCells',
    title: formatMessage(ContextualMenuMessages.mergeCells),
    onClick: mergeCellsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB),
    selected: false,
    disabled: !canMergeCells(editorState.tr)
  }, {
    id: 'editor.table.splitCell',
    title: formatMessage(ContextualMenuMessages.splitCell),
    onClick: splitCellWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB),
    selected: false,
    disabled: !splitCell(editorState)
  }];
  if (pluginState !== null && pluginState !== void 0 && (_pluginState$pluginCo = pluginState.pluginConfig) !== null && _pluginState$pluginCo !== void 0 && _pluginState$pluginCo.allowDistributeColumns) {
    var _newResizeStateWithAn;
    var newResizeStateWithAnalytics = editorView ? getNewResizeStateFromSelectedColumns(initialSelectionRect, editorState, editorView.domAtPos.bind(editorView), getEditorContainerWidth) : undefined;
    var wouldChange = (_newResizeStateWithAn = newResizeStateWithAnalytics === null || newResizeStateWithAnalytics === void 0 ? void 0 : newResizeStateWithAnalytics.changed) !== null && _newResizeStateWithAn !== void 0 ? _newResizeStateWithAn : false;
    var distributeColumnWidths = function distributeColumnWidths(state, dispatch) {
      if (newResizeStateWithAnalytics) {
        distributeColumnsWidthsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, newResizeStateWithAnalytics)(state, dispatch);
        return true;
      }
      return false;
    };
    options.push({
      id: 'editor.table.distributeColumns',
      title: formatMessage(ContextualMenuMessages.distributeColumns),
      onClick: distributeColumnWidths,
      selected: false,
      disabled: !wouldChange
    });
  }
  if (pluginState !== null && pluginState !== void 0 && (_pluginState$pluginCo2 = pluginState.pluginConfig) !== null && _pluginState$pluginCo2 !== void 0 && _pluginState$pluginCo2.allowColumnSorting) {
    var hasMergedCellsInTable = getMergedCellsPositions(editorState.tr).length > 0;
    var warning = hasMergedCellsInTable ? formatMessage(ContextualMenuMessages.canNotSortTable) : undefined;
    options.push({
      id: 'editor.table.sortColumnAsc',
      title: formatMessage(ContextualMenuMessages.sortColumnASC),
      onMouseOver: function onMouseOver(state, dispatch) {
        if (getMergedCellsPositions(state.tr).length !== 0) {
          hoverMergedCells()(state, dispatch);
          return true;
        }
        return false;
      },
      onMouseOut: function onMouseOut(state, dispatch) {
        clearHoverSelection()(state, dispatch);
        return true;
      },
      onClick: function onClick(state, dispatch) {
        sortColumnWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, initialSelectionRect.left, SortOrder.ASC)(state, dispatch);
        return true;
      },
      selected: false,
      disabled: hasMergedCellsInTable,
      tooltip: warning
    });
    options.push({
      id: 'editor.table.sortColumnDesc',
      title: formatMessage(ContextualMenuMessages.sortColumnDESC),
      onMouseOver: function onMouseOver(state, dispatch) {
        if (getMergedCellsPositions(state.tr).length !== 0) {
          hoverMergedCells()(state, dispatch);
          return true;
        }
        return false;
      },
      onMouseOut: function onMouseOut(state, dispatch) {
        clearHoverSelection()(state, dispatch);
        return true;
      },
      onClick: function onClick(state, dispatch) {
        sortColumnWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, initialSelectionRect.left, SortOrder.DESC)(state, dispatch);
        return true;
      },
      selected: false,
      disabled: hasMergedCellsInTable,
      tooltip: warning
    });
  }
  options.push({
    id: 'editor.table.clearCells',
    title: formatMessage(ContextualMenuMessages.clearCells, {
      0: Math.max(numberOfColumns, numberOfRows)
    }),
    onClick: function onClick(state, dispatch) {
      var _getPluginState = getPluginState(state),
        targetCellPosition = _getPluginState.targetCellPosition;
      emptyMultipleCellsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, targetCellPosition)(state, dispatch);
      return true;
    },
    selected: false,
    disabled: false,
    elemAfter: jsx("div", {
      css: shortcutStyle
    }, tooltip(backspace))
  });
  return {
    id: 'editor.table.cellOptions',
    testId: 'cell_options',
    type: 'dropdown',
    title: formatMessage(tableMessages.cellOptions),
    options: options,
    // Increased dropdown item width to prevent labels from being truncated
    dropdownWidth: 230,
    showSelected: false
  };
};
var getClosestSelectionRect = function getClosestSelectionRect(state) {
  var selection = state.selection;
  return isSelectionType(selection, 'cell') ? getSelectionRect(selection) : findCellRectClosestToPos(selection.$from);
};
export var getToolbarConfig = function getToolbarConfig(getEditorContainerWidth, editorAnalyticsAPI, getEditorFeatureFlags, getEditorView) {
  return function (config) {
    return function (state, intl) {
      var tableObject = findTable(state.selection);
      var pluginState = getPluginState(state);
      var resizeState = tableResizingPluginKey.getState(state);
      var tableWidthState = tableWidthPluginKey.getState(state);

      // We don't want to show floating toolbar while resizing the table
      var isWidthResizing = tableWidthState === null || tableWidthState === void 0 ? void 0 : tableWidthState.resizing;
      if (tableObject && pluginState.editorHasFocus && !isWidthResizing) {
        var nodeType = state.schema.nodes.table;
        var menu = getToolbarMenuConfig(config, pluginState, intl, editorAnalyticsAPI);
        var cellItems = getCellItems(config, state, getEditorView(), intl, getEditorContainerWidth, editorAnalyticsAPI);
        var colorPicker = getColorPicker(state, menu, intl, editorAnalyticsAPI);

        // Check if we need to show confirm dialog for delete button
        var confirmDialog;
        if (isReferencedSource(state, tableObject.node)) {
          var localSourceName = intl.formatMessage(tableMessages.unnamedSource);
          confirmDialog = function confirmDialog() {
            return {
              title: intl.formatMessage(tableMessages.deleteElementTitle),
              okButtonLabel: intl.formatMessage(tableMessages.confirmDeleteLinkedModalOKButton),
              message: intl.formatMessage(tableMessages.confirmDeleteLinkedModalMessage, {
                nodeName: getNodeName(state, tableObject.node) || localSourceName
              }),
              messagePrefix: intl.formatMessage(tableMessages.confirmDeleteLinkedModalMessagePrefix),
              isReferentialityDialog: true,
              getChildrenInfo: function getChildrenInfo() {
                return _getChildrenInfo(state, tableObject.node);
              },
              checkboxLabel: intl.formatMessage(tableMessages.confirmModalCheckboxLabel),
              onConfirm: function onConfirm() {
                var isChecked = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
                return clickWithCheckboxHandler(isChecked, tableObject.node);
              }
            };
          };
        }
        var getDomRef = function getDomRef(editorView) {
          var element;
          var domAtPos = editorView.domAtPos.bind(editorView);
          var parent = findParentDomRefOfType(nodeType, domAtPos)(state.selection);
          if (parent) {
            var tableRef = parent.querySelector('table') || undefined;
            if (tableRef) {
              element = closestElement(tableRef, ".".concat(TableCssClassName.TABLE_NODE_WRAPPER)) || undefined;
            }
          }
          return element;
        };
        return {
          title: 'Table floating controls',
          getDomRef: getDomRef,
          nodeType: nodeType,
          offset: [0, 18],
          zIndex: akEditorFloatingPanelZIndex + 1,
          // Place the context menu slightly above the others
          items: [menu, separator(menu.hidden)].concat(cellItems, colorPicker, [{
            type: 'extensions-placeholder',
            separator: 'end'
          }, {
            type: 'copy-button',
            items: [{
              state: state,
              formatMessage: intl.formatMessage,
              nodeType: nodeType,
              onMouseEnter: hoverTable(false, true),
              onMouseLeave: clearHoverSelection(),
              onFocus: hoverTable(false, true),
              onBlur: clearHoverSelection()
            }, {
              type: 'separator'
            }]
          }, {
            id: 'editor.table.delete',
            type: 'button',
            appearance: 'danger',
            icon: RemoveIcon,
            onClick: deleteTableWithAnalytics(editorAnalyticsAPI),
            disabled: !!resizeState && !!resizeState.dragging,
            onMouseEnter: hoverTable(true),
            onFocus: hoverTable(true),
            onBlur: clearHoverSelection(),
            onMouseLeave: clearHoverSelection(),
            title: intl.formatMessage(commonMessages.remove),
            focusEditoronEnter: true,
            confirmDialog: confirmDialog
          }]),
          scrollable: true
        };
      }
      return;
    };
  };
};
var separator = function separator(hidden) {
  return {
    type: 'separator',
    hidden: hidden
  };
};
var getCellItems = function getCellItems(pluginConfig, state, view, _ref3, getEditorContainerWidth, editorAnalyticsAPI) {
  var formatMessage = _ref3.formatMessage;
  var initialSelectionRect = getClosestSelectionRect(state);
  if (initialSelectionRect) {
    var cellOptions = getToolbarCellOptionsConfig(state, view, initialSelectionRect, {
      formatMessage: formatMessage
    }, getEditorContainerWidth, editorAnalyticsAPI);
    return [cellOptions, separator(cellOptions.hidden)];
  }
  return [];
};
var getColorPicker = function getColorPicker(state, menu, _ref4, editorAnalyticsAPI) {
  var _node$attrs;
  var formatMessage = _ref4.formatMessage;
  var _getPluginState2 = getPluginState(state),
    targetCellPosition = _getPluginState2.targetCellPosition,
    pluginConfig = _getPluginState2.pluginConfig;
  if (!pluginConfig.allowBackgroundColor) {
    return [];
  }
  var node = targetCellPosition ? state.doc.nodeAt(targetCellPosition) : undefined;
  var currentBackground = (node === null || node === void 0 ? void 0 : (_node$attrs = node.attrs) === null || _node$attrs === void 0 ? void 0 : _node$attrs.background) || '#ffffff';
  var defaultPalette = cellBackgroundColorPalette.find(function (item) {
    return item.value === currentBackground;
  }) || {
    label: 'Custom',
    value: currentBackground,
    border: DEFAULT_BORDER_COLOR
  };
  return [{
    id: 'editor.table.colorPicker',
    title: formatMessage(ContextualMenuMessages.cellBackground),
    type: 'select',
    selectType: 'color',
    defaultValue: defaultPalette,
    options: cellBackgroundColorPalette,
    onChange: function onChange(option) {
      return setColorWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, option.value, targetCellPosition);
    }
  }, separator(menu.hidden)];
};
var clickWithCheckboxHandler = function clickWithCheckboxHandler(isChecked, node, editorAnalyticsAPI) {
  return function (state, dispatch) {
    if (!node) {
      return false;
    }
    if (!isChecked) {
      return deleteTableWithAnalytics(editorAnalyticsAPI)(state, dispatch);
    } else {
      removeDescendantNodes(node)(state, dispatch);
    }
    return true;
  };
};
var highlightRowsHandler = function highlightRowsHandler(state, dispatch) {
  var selectionRect = getClosestSelectionRect(state);
  if (selectionRect) {
    hoverRows(getSelectedRowIndexes(selectionRect), true)(state, dispatch);
    return true;
  }
  return false;
};
var highlightColumnsHandler = function highlightColumnsHandler(state, dispatch) {
  var selectionRect = getClosestSelectionRect(state);
  if (selectionRect) {
    hoverColumns(getSelectedColumnIndexes(selectionRect), true)(state, dispatch);
    return true;
  }
  return false;
};